/**
 * The small, shared building blocks: cards, badges, tables, skeletons and empty
 * states. Everything here is presentational — no data fetching, no business
 * rules, and no client-side state.
 *
 * No `'use client'` directive, so these render on the server for the ~30 pages
 * that only need markup, and are pulled into the bundle only where a client
 * component actually imports them. `Progress` and `Separator` need Radix and
 * therefore live in `./progress`.
 */
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-card)] border border-border bg-surface shadow-[var(--shadow-card)]',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({
  title,
  description,
  action,
  className,
  as: Heading = 'h2',
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  as?: 'h1' | 'h2' | 'h3';
}) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-5', className)}>
      <div className="min-w-0">
        <Heading className="text-[15px] font-semibold text-ink">{title}</Heading>
        {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function CardBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-4 py-4 sm:px-5', className)} {...props} />;
}

export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface-muted px-4 py-3 sm:px-5', className)}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-muted text-ink-muted border border-border',
        brand: 'bg-brand-soft text-brand-ink border border-brand/20',
        positive: 'bg-positive-soft text-positive-ink border border-positive/25',
        warning: 'bg-warning-soft text-warning-ink border border-warning/30',
        danger: 'bg-danger-soft text-danger-ink border border-danger/25',
        info: 'bg-info-soft text-info-ink border border-info/25',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export type BadgeProps = React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>;

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

/**
 * Tables scroll inside their own container rather than pushing the page wide.
 * `tabindex=0` on the scroller lets a keyboard user reach the overflow.
 */
export function TableScroller({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn('w-full overflow-x-auto', className)}
      tabIndex={0}
      role="region"
      aria-label="Scrollable table"
    >
      {children}
    </div>
  );
}

export function Table({ className, ...props }: React.TableHTMLAttributes<HTMLTableElement>) {
  return <table data-slot="table" className={cn('w-full border-collapse text-sm', className)} {...props} />;
}

export function Th({
  className,
  numeric,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <th
      scope="col"
      className={cn(
        'border-b border-border bg-surface-muted px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted',
        numeric && 'text-right',
        className,
      )}
      {...props}
    />
  );
}

export function Td({
  className,
  numeric,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <td
      className={cn('border-b border-border px-3 py-2.5 align-middle text-ink', numeric && 'text-right', className)}
      {...props}
    />
  );
}

export function Tr({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('hover:bg-surface-muted/60', className)} {...props} />;
}

/** The bold summary row under a table. */
export function TotalRow({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('bg-surface-muted font-semibold text-ink [&>td]:border-t-2 [&>td]:border-border-strong', className)} {...props} />;
}

// ---------------------------------------------------------------------------
// Empty & loading states
// ---------------------------------------------------------------------------

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      {Icon ? (
        <div className="mb-3 flex size-11 items-center justify-center rounded-full bg-surface-muted">
          <Icon className="size-5 text-ink-subtle" />
        </div>
      ) : null}
      <p className="text-[15px] font-semibold text-ink">{title}</p>
      {description ? <p className="mt-1 max-w-sm text-sm text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('animate-pulse rounded bg-surface-muted', className)} aria-hidden="true" {...props} />;
}

export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-2 p-4" role="status" aria-label="Loading">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-3">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton key={columnIndex} className={cn('h-5 flex-1', columnIndex === 0 && 'flex-[2]')} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page furniture
// ---------------------------------------------------------------------------

export function PageHeader({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">{title}</h1>
          {description ? <p className="mt-1 text-sm text-ink-muted">{description}</p> : null}
        </div>
        {/* `min-w-0` rather than `shrink-0`: a wide action row — six date presets,
            say — must be allowed to become narrower than its content so that its
            own `flex-wrap` has something to wrap into. With `shrink-0` it grows
            past the viewport instead, and the whole page scrolls sideways. */}
        {actions ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/** A label/value pair, used in detail panels and summaries. */
export function DetailRow({
  label,
  value,
  emphasis = false,
  divider = false,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  emphasis?: boolean;
  /** Draws a rule above the row — used to set a total apart from its inputs. */
  divider?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4 py-1.5',
        divider && 'mt-1 border-t border-border pt-2.5',
      )}
    >
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd
        data-detail={typeof label === 'string' ? label : undefined}
        className={cn('text-sm tabular text-ink', emphasis && 'text-base font-semibold')}
      >
        {value}
      </dd>
    </div>
  );
}

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'positive';
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const tones = {
    info: 'border-info/25 bg-info-soft text-info-ink',
    warning: 'border-warning/30 bg-warning-soft text-warning-ink',
    danger: 'border-danger/25 bg-danger-soft text-danger-ink',
    positive: 'border-positive/25 bg-positive-soft text-positive-ink',
  }[tone];

  return (
    <div
      className={cn('rounded-[var(--radius-control)] border px-3.5 py-3 text-sm', tones, className)}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      {title ? <p className="font-semibold">{title}</p> : null}
      <div className={cn(title && 'mt-0.5')}>{children}</div>
    </div>
  );
}

export { Progress, Separator } from './progress';
